import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Divider,
  Box,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  MoreVert,
  Info as InfoIcon,
  ReceiptLong as InvoiceIcon,
} from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import { useAuth } from '../../context/AuthContext';
import indexedDBService from '../../utils/indexedDBService';
import { Link } from 'react-router-dom';

// Import Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface OutstandingInvoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  amount: number;
  dueDate: Date;
  daysOverdue: number;
}

const OutstandingInvoicesWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [outstandingInvoices, setOutstandingInvoices] = useState<OutstandingInvoice[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    fetchOutstandingInvoices();
  }, [currentUser]);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefresh = () => {
    handleMenuClose();
    fetchOutstandingInvoices();
  };

  const getTransactionDate = (date: any): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (typeof date === 'string') return new Date(date);
    if (typeof date === 'number') return new Date(date);
    if (date && typeof date.seconds === 'number') return new Date(date.seconds * 1000);
    return new Date(date);
  };

  const fetchOutstandingInvoices = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // Get current date
      const now = new Date();

      // Fetch invoices from IndexedDB
      const allInvoices = await indexedDBService.getAllItems<any>('invoices');
      const userInvoices = allInvoices.filter(inv => inv.userId === currentUser.uid);

      // Process the invoices
      let outstandingInvoicesList: OutstandingInvoice[] = [];

      userInvoices.forEach(data => {
        // Filter for Pending or Overdue
        if (data.status !== 'Pending' && data.status !== 'Overdue') return;

        // Skip invoices with zero or undefined amount
        if (!data.totalAmount) return;

        // Parse due date
        let dueDate: Date | null = null;
        if (data.dueDate) {
          dueDate = getTransactionDate(data.dueDate);
        }

        // If no due date, use invoice date + 30 days as fallback
        if (!dueDate && data.date) {
          const invoiceDate = getTransactionDate(data.date);
          dueDate = new Date(invoiceDate);
          dueDate.setDate(dueDate.getDate() + 30); // Default 30 days payment term
        }

        // Skip invoices without due date
        if (!dueDate) return;

        // Calculate days overdue
        const diffTime = now.getTime() - dueDate.getTime();
        const daysOverdue = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        outstandingInvoicesList.push({
          id: data.id,
          invoiceNumber: data.invoiceNumber || `INV-${data.id.substring(0, 5)}`,
          customerName: data.customerName || 'Customer',
          amount: Number(data.totalAmount) || 0,
          dueDate,
          daysOverdue
        });
      });

      // Sort by amount (highest first)
      outstandingInvoicesList.sort((a, b) => b.amount - a.amount);

      // Take top 5
      setOutstandingInvoices(outstandingInvoicesList.slice(0, 5));

    } catch (error) {
      console.error('Error fetching outstanding invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Prepare chart data
  const chartData = {
    labels: outstandingInvoices.map(invoice => invoice.invoiceNumber),
    datasets: [
      {
        label: 'Amount',
        data: outstandingInvoices.map(invoice => invoice.amount),
        backgroundColor: outstandingInvoices.map(invoice =>
          invoice.daysOverdue > 0 ? 'rgba(229, 83, 83, 0.7)' : 'rgba(71, 118, 230, 0.7)'
        ),
        borderColor: outstandingInvoices.map(invoice =>
          invoice.daysOverdue > 0 ? 'rgba(229, 83, 83, 1)' : 'rgba(71, 118, 230, 1)'
        ),
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function (tooltipItems: any[]) {
            const idx = tooltipItems[0].dataIndex;
            const invoice = outstandingInvoices[idx];
            return `${invoice.invoiceNumber} - ${invoice.customerName}`;
          },
          label: function (context: any) {
            const invoice = outstandingInvoices[context.dataIndex];
            let label = formatCurrency(invoice.amount);

            const dueDateStr = invoice.dueDate.toLocaleDateString();
            label += `\nDue: ${dueDateStr}`;

            if (invoice.daysOverdue > 0) {
              label += `\n${invoice.daysOverdue} days overdue`;
            }

            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (this: any, tickValue: any) {
            return formatCurrency(Number(tickValue));
          }
        },
        grid: {
          display: true,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardHeader
        title="Outstanding Invoices"
        subheader="Top 5 by amount"
        action={
          <IconButton onClick={handleMenuClick}>
            <MoreVert />
          </IconButton>
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'outstanding-invoices-menu-button',
        }}
      >
        <MenuItem onClick={handleRefresh}>Refresh</MenuItem>
      </Menu>
      <Divider />
      <CardContent sx={{ p: 3 }}>
        {loading ? (
          <Box height={280} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : outstandingInvoices.length === 0 ? (
          <Box height={280} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
            <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" align="center">
              No outstanding invoices found.
            </Typography>
          </Box>
        ) : (
          <>
            <Box height={250}>
              <Bar data={chartData} options={chartOptions} />
            </Box>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                startIcon={<InvoiceIcon />}
                component={Link}
                to="/invoices"
                color="primary"
                size="small"
              >
                View All Invoices
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OutstandingInvoicesWidget; 
